import { z, ZodError, ZodObject } from 'zod';

export function ZodModel<
  T extends z.ZodRawShape,
  Type = ZodClassConstructor<T>,
>(schema: z.ZodObject<T>): Type {
  const partialSchema = schema.partial();

  return class {
    static schema = schema;

    static empty(data: Partial<z.input<z.ZodObject<T>>> = {}, ...args: any[]) {
      return new (this as any)({ ...data, __empty: true }, ...args);
    }

    constructor(value: Partial<z.infer<z.ZodObject<T>>> = {}) {
      this.update(value);
    }

    toJSON(): z.infer<z.ZodObject<T>> {
      return { ...this } as any;
    }

    update(dto: Partial<z.infer<z.ZodObject<T>>> = {}) {
      const res = schema.safeParse(dto);
      if (res.success) {
        Object.assign(this, res.data);
        return;
      }

      if (!(dto as { __empty?: boolean }).__empty) {
        onParseError({ dto, error: res.error, instance: this, schema });
      }

      // Ideally, it would be better to use only `schema.parse(dto)`,
      // but I'm still not sure whether it's time to switch to such a strict
      // checking. Just collecting error currently.
      const updateData: Record<string, unknown> = {};
      Object.entries(partialSchema.shape).forEach(([key, fieldSchema]) => {
        const value = (dto as Record<string, unknown>)?.[key];
        const { data, success } = fieldSchema.safeParse(value);
        if (success) {
          updateData[key] = data;
        }
      });

      Object.assign(this, updateData);
    }
  } as Type;
}

ZodModel.errorHandler = (_ctx: ZodModelErrorContext) => {};

function onParseError(ctx: Omit<ZodModelErrorContext, 'message'>) {
  const message = `${ctx.constructor.name}.update: ${ctx.error.message}`;
  ZodModel.errorHandler({ ...ctx, message });
}

export interface ZodClassConstructor<
  T extends z.ZodRawShape,
  TSchema = z.ZodObject<T>,
> {
  schema: TSchema;

  new (
    data: z.input<z.ZodObject<T>>,
  ): z.infer<z.ZodObject<T>> & ZodModelInstance<T>;

  // Currently, TConst is a workaround to return the final type
  empty<TConst>(
    data?: Partial<z.input<z.ZodObject<T>>>,
    ...args: any[]
  ): TConst & z.infer<z.ZodObject<T>> & ZodModelInstance<T>;
}

interface ZodModelErrorContext {
  dto: object;
  error: ZodError;
  instance: object;
  message: string;
  schema: ZodObject<any>;
}

export interface ZodModelInstance<T extends z.ZodRawShape = z.ZodRawShape> {
  __createType: z.input<z.ZodObject<T>>;
  __populatedType: z.infer<z.ZodObject<T>>;
  toJSON(): z.infer<z.ZodObject<T>>;
  update(dto: Partial<z.infer<z.ZodObject<T>>>): void;
}
